/* eslint-disable jsx-a11y/no-static-element-interactions */
import ONBOARD_IMG from "@assets/quick-news/Onboard.png";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { formatDate } from "@helper/useDateFormat";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { getAuthorsName } from "@routes/Article/ArticleMetaData";
import QuickNewsMetaData from "@routes/QuickNews/QuickNewsMetaData";
import { QNCardType } from "@typings/QuickNews";
import Cookies from "js-cookie";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import QnCardNav from "./QnCardNav";

// Constants
const ANIMATION_DURATION = 300;
const ENTRANCE_DELAY = 100;
const DRAG_THRESHOLD = 50;
const DRAG_UP_THRESHOLD = -30;
const ONBOARD_VIRTUAL_URL = "/quick-news";

// Types
interface DragPosition {
  x: number;
  y: number;
}

interface UseDragGestureReturn {
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseUp: (e: React.MouseEvent) => void;
}

// Custom hook for drag gesture handling
const useDragGesture = (onSwipeUp: () => void): UseDragGestureReturn => {
  const [dragStart, setDragStart] = useState<DragPosition | null>(null);

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    setDragStart({ x: clientX, y: clientY });
  }, []);

  const handleDragEnd = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragStart) return;

      const deltaX = clientX - dragStart.x;
      const deltaY = clientY - dragStart.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Check if it's a swipe up gesture
      if (distance > DRAG_THRESHOLD && deltaY < DRAG_UP_THRESHOLD) {
        onSwipeUp();
      }

      setDragStart(null);
    },
    [dragStart, onSwipeUp],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleDragStart(touch.clientX, touch.clientY);
    },
    [handleDragStart],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      handleDragEnd(touch.clientX, touch.clientY);
    },
    [handleDragEnd],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleDragStart(e.clientX, e.clientY);
    },
    [handleDragStart],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      handleDragEnd(e.clientX, e.clientY);
    },
    [handleDragEnd],
  );

  return {
    handleTouchStart,
    handleTouchEnd,
    handleMouseDown,
    handleMouseUp,
  };
};

export default function QnOnboard({
  isMobile = false,
  activeTab,
  initialId,
  setPushState,
  article,
}: {
  isMobile?: boolean;
  activeTab: QNCardType;
  initialId?: string;
  setPushState: (activeTab: QNCardType) => void;
  article?: ProcessedArticleData;
}): ReactElement {
  const [onBoarded, setOnBoarded] = useState(initialId ? true : false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(true);
  const [mainLottieLoaded, setMainLottieLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const readMoreCanvasRef = useRef<HTMLCanvasElement>(null);
  const mainLottieInstanceRef = useRef<LottieInstance | null>(null);
  const readMoreLottieInstanceRef = useRef<LottieInstance | null>(null);
  const hasTrackedOnboardPvRef = useRef(false);
  const lastSyncedArticleIdRef = useRef<string | null>(null);
  const TmCustomEvent = useTMCustomEventAnalyticsCall();
  const cookieName = "sph_qn_onboarded";

  const trackVirtualPV = useCallback(
    (articleData: ProcessedArticleData) => {
      TmCustomEvent({
        event: "virtual_pv",
        articleid: articleData.id,
        cue_articleid: articleData.id,
        pagination: 1,
        author:
          "authors" in articleData
            ? getAuthorsName(articleData.authors || [])
            : "",
        level2: "quick-news",
        chapter1: "",
        contentcat: 1,
        contenttype: undefined,
        pubdate:
          "datetime" in articleData
            ? formatDate(articleData.datetime, "yyyy-MM-dd HH:mm:ss")
            : "",
        title: articleData.title,
        virtual_url: `${window.location.origin}${ONBOARD_VIRTUAL_URL}/${
          articleData.id == "tamil-quiz" ? "word-quiz" : articleData.id
        }`,
      });
    },
    [TmCustomEvent],
  );
  const setMetaTags = useCallback(
    (card: ProcessedArticleData) => {
      const { metaTags } = QuickNewsMetaData({
        card,
        activeTab,
        onBoarded: true,
        pagination: 0,
      });

      document.title = metaTags.title || "";
      const metaDescription = document.querySelector<HTMLMetaElement>(
        'meta[name="description"]',
      );
      if (metaDescription) {
        metaDescription.content = metaTags.description || "";
      }
      const metaCanonical = document.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      );
      if (metaCanonical) {
        metaCanonical.href = `${window.location.origin}/${metaTags.slug || ""}`;
      }
    },
    [activeTab],
  );

  const closeOnBoard = useCallback(() => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setOnBoarded(true);
      try {
        setPushState(activeTab);
      } catch (error) {
        console.error("Failed to set onboarding cookie:", error);
      }
    }, ANIMATION_DURATION);

    // On board close set the cookie.
    Cookies.set(cookieName, "true");

    if (activeTab === "MyFeed" && article) {
      setMetaTags(article);
      return;
    }
  }, [activeTab, article, setMetaTags, setPushState]);

  // Custom hooks
  const { handleTouchStart, handleTouchEnd, handleMouseDown, handleMouseUp } =
    useDragGesture(closeOnBoard);

  // Load Lottie animations
  useEffect(() => {
    if (onBoarded || typeof window === "undefined") return;

    let mounted = true;

    const loadAnimations = () => {
      // Load main animation
      const canvasHeight = window.innerHeight < 700 ? 500 : 600;
      const mainConfig: LottieConfig = {
        animationPath: `quick-news/${isMobile ? "qnWelcomeMobile.lottie" : "qnWelcome.lottie"}`,
        canvasId: "quick-news-onboarding",
        dimensions: {
          height: canvasHeight,
          minHeight: canvasHeight,
        },
      };

      const mainInstance = loadLottieAnimation(mainConfig);
      if (mounted && mainInstance) {
        mainLottieInstanceRef.current = mainInstance;
        console.log("Setting main lottie loaded");
        setTimeout(() => {
          console.log("timeout fired");
          setMainLottieLoaded(true);
        }, 200);
      }

      // Load read more animation
      const readMoreConfig: LottieConfig = {
        animationPath: "quick-news/readMore.lottie",
        canvasId: "qn-onboarding-read-more",
        dimensions: {
          height: 29,
          minHeight: 29,
        },
      };

      const readMoreInstance = loadLottieAnimation(readMoreConfig);
      if (mounted && readMoreInstance) {
        readMoreLottieInstanceRef.current = readMoreInstance;
      }
    };

    loadAnimations();

    // Cleanup function
    return () => {
      mounted = false;
      if (mainLottieInstanceRef.current) {
        mainLottieInstanceRef.current.destroy();
        mainLottieInstanceRef.current = null;
      }
      if (readMoreLottieInstanceRef.current) {
        readMoreLottieInstanceRef.current.destroy();
        readMoreLottieInstanceRef.current = null;
      }
    };
  }, [onBoarded, isMobile]);

  // Initialize component state
  useEffect(() => {
    try {
      // Trigger entrance animation after a small delay
      setTimeout(() => {
        setIsAnimatingIn(false);
      }, ENTRANCE_DELAY);
    } catch (error) {
      console.error("Failed to read onboarding cookie:", error);
      // Default to showing onboarding if cookie read fails
      setTimeout(() => {
        setIsAnimatingIn(false);
      }, ENTRANCE_DELAY);
    }
  }, []);

  // Keyboard support
  useEffect(() => {
    if (onBoarded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" ||
        e.key === "Enter" ||
        e.key === "ArrowDown" ||
        e.key === " "
      ) {
        e.preventDefault();
        closeOnBoard();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onBoarded, closeOnBoard]);

  useEffect(() => {
    const existingCookie = Cookies.get(cookieName);

    const syncArticleState = () => {
      if (!article) return;
      if (lastSyncedArticleIdRef.current === article.id) return;

      lastSyncedArticleIdRef.current = article.id;
      setMetaTags(article);
      window.history.pushState(
        "article",
        article.title,
        `${ONBOARD_VIRTUAL_URL}/${article.id}${window.location.search}`,
      );
      trackVirtualPV(article);
    };

    if (existingCookie && !onBoarded) {
      setOnBoarded(true);
    }
    if (existingCookie || onBoarded) {
      syncArticleState();
      return;
    }

    if (hasTrackedOnboardPvRef.current) {
      return;
    }

    hasTrackedOnboardPvRef.current = true;
    TmCustomEvent({
      event: "virtual_pv",
      pagination: 1,
      level2: "quick-news",
      chapter1: "",
      contentcat: 1,
      contenttype: undefined,
      title: "Quick News",
      virtual_url: `${window.location.origin}${ONBOARD_VIRTUAL_URL}`,
    });
  }, [article, setMetaTags, TmCustomEvent, trackVirtualPV, onBoarded]);

  // Don't render if user has already onboarded
  if (onBoarded) {
    return <></>;
  }

  return (
    <>
      <div
        className={`absolute z-50 w-full h-[100vh] bg-[linear-gradient(168deg,#272727_0%,#101010_100%)]`}
      ></div>
      <div
        className={twMerge(
          "fixed z-50 w-full h-[100vh] m-auto bg-[linear-gradient(168deg,#272727_0%,#101010_100%)] transition-all duration-300 ease-in-out",
          // Entrance animation: start from bottom
          isAnimatingIn && "top-full scale-90 opacity-0",
          // Exit animation: slide up and scale down
          isAnimatingOut && "-top-full scale-75 opacity-80",
          // Normal state: visible and centered
          !isAnimatingIn && !isAnimatingOut && "top-0 scale-100 opacity-100",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        aria-describedby="onboarding-description"
      >
        <div
          data-test-id="quick-news-onboarding"
          className="grid my-auto h-full text-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          tabIndex={0}
          role="button"
          aria-label={
            isMobile
              ? "Swipe up or tap to start reading Quick News"
              : "Click the arrow button to start reading Quick News"
          }
        >
          <div className="w-full my-auto relative">
            <canvas
              id="quick-news-onboarding"
              ref={canvasRef}
              className={twMerge(
                "mx-auto w-full md:w-[450px] transition-opacity duration-500",
              )}
              aria-hidden="true"
            />
            {/* {!mainLottieLoaded && ( */}
            <img
              src={ONBOARD_IMG}
              alt="Welcome to Quick News - Tamil news in a swipeable card format"
              className={twMerge(
                `absolute inset-0 w-[280px] m-auto object-contain block`,
                mainLottieLoaded && "hidden",
              )}
            />
            {/* )} */}
          </div>

          <div className="z-20">
            <button
              aria-label="Start reading Quick News"
              className="absolute top-[calc(100svh-65px)] left-1/2 transform -translate-x-1/2 items-center justify-center text-white-100 focus:outline-none focus:ring-2 focus:ring-white-100 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
              onClick={closeOnBoard}
            >
              படிக்கத் தொடங்கவும்
              <canvas
                id="qn-onboarding-read-more"
                ref={readMoreCanvasRef}
                className={twMerge("mx-auto w-[11px]")}
                aria-hidden="true"
              />
            </button>
          </div>
          <QnCardNav
            activeTab={activeTab}
            canPrevFeed={false}
            canPrevVideo={false}
            canNextFeed={true}
            canNextVideo={false}
            onPrev={() => {}}
            onNext={closeOnBoard}
          />

          {/* Screen reader only content */}
          <div className="sr-only">
            <h1 id="onboarding-title">Welcome to Quick News</h1>
            <p id="onboarding-description">
              {isMobile
                ? "Swipe up or tap the button below to start reading Tamil news in an interactive card format."
                : "Click the arrow button to start reading Tamil news in an interactive card format."}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
